import React, { useState } from 'react';
import HomeTabPreview from './tabs/HomeTabPreview';
import { renderSimpleFormattedText } from '../utils/textFormatter';
import { filterBadWords } from '../utils/badWordsFilter';
import { calculateNextOccurrence, formatRecurrencePattern, isRecurringEvent } from '../utils/recurrenceUtils';

const isValueProvided = (value) => {
  return value !== undefined && value !== null && value !== '';
};

const getParticipantRatingLabel = (participant) => {
  const federations = participant?.federations || {};

  if (isValueProvided(participant.fideRating)) {
    return participant.fideRating;
  }
  if (isValueProvided(federations?.FIDE?.rating)) {
    return federations.FIDE.rating;
  }
  if (isValueProvided(participant.uscfRating)) {
    return participant.uscfRating;
  }
  if (isValueProvided(federations?.USCF?.rating)) {
    return federations.USCF.rating;
  }
  if (isValueProvided(participant.cfcRating)) {
    return participant.cfcRating;
  }
  if (isValueProvided(federations?.CAN?.rating)) {
    return federations.CAN.rating;
  }
  if (isValueProvided(participant.nationalRating)) {
    return participant.nationalRating;
  }
  if (isValueProvided(participant.rating)) {
    return participant.rating;
  }
  return '';
};

const getParticipantRatingValue = (participant) => {
  const federations = participant?.federations || {};

  if (isValueProvided(participant.fideRating)) {
    return parseInt(participant.fideRating, 10) || 0;
  }
  if (isValueProvided(federations?.FIDE?.rating)) {
    return parseInt(federations.FIDE.rating, 10) || 0;
  }
  if (isValueProvided(participant.uscfRating)) {
    return parseInt(participant.uscfRating, 10) || 0;
  }
  if (isValueProvided(federations?.USCF?.rating)) {
    return parseInt(federations.USCF.rating, 10) || 0;
  }
  if (isValueProvided(participant.cfcRating)) {
    return parseInt(participant.cfcRating, 10) || 0;
  }
  if (isValueProvided(federations?.CAN?.rating)) {
    return parseInt(federations.CAN.rating, 10) || 0;
  }
  if (isValueProvided(participant.nationalRating)) {
    return parseInt(participant.nationalRating, 10) || 0;
  }
  if (isValueProvided(participant.rating)) {
    return parseInt(participant.rating, 10) || 0;
  }
  return 0;
};

const getParticipantFederationIdLabel = (participant) => {
  const federations = participant?.federations || {};

  const primaryCandidates = [
    participant.fideId,
    federations?.FIDE?.id,
    participant.uscfId,
    federations?.USCF?.id,
  ];

  const primaryValue = primaryCandidates.find(isValueProvided);
  if (primaryValue) {
    return primaryValue;
  }

  const otherCandidates = [
    federations?.CAN?.id,
    federations?.CFC?.id,
    federations?.OTHER?.id,
    participant.cfcId,
    participant.localFederationId,
    participant.otherFederationId,
    participant.federationId,
    participant.nationalId,
  ];

  const otherValue = otherCandidates.find(isValueProvided);
  if (otherValue) {
    return `OTHER: ${otherValue}`;
  }

  const otherFederationValue = Object.entries(federations)
    .filter(([key]) => typeof key === 'string' && !['FIDE', 'USCF', 'CAN', 'CFC', 'OTHER'].includes(key.toUpperCase()))
    .map(([, value]) => value?.id)
    .find(isValueProvided);

  if (otherFederationValue) {
    return `OTHER: ${otherFederationValue}`;
  }

  return 'N/A';
};

const getFederationIdHeaderLabel = (participants = [], federationType) => {
  if (federationType === 'FIDE') {
    return 'FIDE ID';
  }

  if (
    federationType === 'National' ||
    federationType === 'USCF' ||
    federationType === 'uscf'
  ) {
    return 'USCF ID';
  }

  for (const participant of participants) {
    if (
      isValueProvided(participant?.fideId) ||
      isValueProvided(participant?.federations?.FIDE?.id)
    ) {
      return 'FIDE ID';
    }
    if (
      isValueProvided(participant?.uscfId) ||
      isValueProvided(participant?.federations?.USCF?.id)
    ) {
      return 'USCF ID';
    }
  }

  return 'USCF ID';
};

const getNormalizedSections = (data) => {
  if (Array.isArray(data?.tournamentSections)) {
    return data.tournamentSections;
  }
  if (Array.isArray(data?.sections)) {
    return data.sections;
  }
  return [];
};

const getParticipantSectionLabel = (participant, sections = []) => {
  const normalizedSections = Array.isArray(sections) ? sections : [];

  let sectionId = null;
  let sectionName = null;

  if (participant?.section) {
    if (typeof participant.section === 'string') {
      sectionId = participant.section;
    } else if (typeof participant.section === 'object' && participant.section !== null) {
      sectionId = participant.section.id || participant.section.value;
      if (participant.section.name) {
        sectionName = participant.section.name;
      }
    }
  }

  if (!sectionId) {
    sectionId = participant?.sectionId || participant?.sectionCode || participant?.selectedSection;
  }

  if (sectionId) {
    const match = normalizedSections.find(section => section && (section.id === sectionId || section.name === sectionId));
    if (match?.name) {
      return match.name;
    }
  }

  if (sectionName) return sectionName;
  if (participant?.sectionName) return participant.sectionName;
  if (sectionId) return sectionId;
  return 'N/A';
};

// Component to render custom sections
const CustomSectionsRenderer = ({ sections, primaryColorHex }) => {


  if (!sections || !Array.isArray(sections) || sections.length === 0) {
    return null;
  }

  // Filter out sections that have no content
  const sectionsWithContent = sections.filter(section => {
    if (section.heading && typeof section.heading === 'string' && section.heading.trim() !== '') return true;
    return section.contentFields && section.contentFields.some(field => 
      field.content && typeof field.content === 'string' && field.content.trim() !== ''
    );
  });

  if (sectionsWithContent.length === 0) {
    return null;
  }

  
  return (
    <div className="space-y-6">
      {sectionsWithContent.map((section, index) => (
        <div key={section.id || index} style={{
            borderRadius: '12px',
          border: '1px solid #E5E7EB',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
          padding: '24px',
          marginBottom: '24px',
          maxWidth: '850px',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          {section.heading && (
            <h2 style={{
              fontSize: '18px',
              fontWeight: 600,
              textAlign: 'center',
              marginBottom: '16px',
              color: '#111827'
            }}>{section.heading}</h2>
          )}
          <div className="space-y-4">
            {section.contentFields && section.contentFields.map((field, fieldIndex) => (
              field.content && typeof field.content === 'string' && field.content.trim() !== '' && (
                <div key={field.id || fieldIndex}>
                  {field.label && (
                    <h3 style={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#6B7280'
                    }}>{field.label}</h3>
                  )}
                  <div style={{
                    fontSize: '16px',
                    color: '#111827',
                    textAlign: 'center'
                  }}>
                    {renderSimpleFormattedText(field.content)}
                  </div>
                </div>
              )
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const PreviewSection = ({ data, toggles, activeTab, onTabChange }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [participantsSearchQuery, setParticipantsSearchQuery] = useState('');

  // Helper function to check if a value has content
  const hasContent = (value) => {
    return value && typeof value === 'string' && value.trim() !== '';
  };

  // Helper function to check if participants array has content
  const hasParticipants = (participants) => {
    return participants && Array.isArray(participants) && participants.length > 0;
  };

  // Helper function to check if custom tabs array has content
  const hasCustomTabs = (customTabs) => {
    return customTabs && Array.isArray(customTabs) && customTabs.length > 0 && 
           customTabs.some(tab => hasContent(tab.label) || hasContent(tab.content));
  };

  // Helper function to check if custom sections have content
  const hasCustomSectionsContent = (tabId) => {
    const customSectionsField = `${tabId}CustomSections`;
    const customSections = data[customSectionsField];
    return customSections && customSections.some(section => {
        if (section.heading && typeof section.heading === 'string' && section.heading.trim() !== '') return true;
        return section.contentFields && section.contentFields.some(field => 
          field.content && typeof field.content === 'string' && field.content.trim() !== ''
        );
      });
  };

  // Rigid tab content validation system (same as FormSection)
  const getTabContentStatus = (tabId) => {
    const isTabModeCustom = data.tabCustomModes?.[tabId] || false;
    const hasCustomSections = hasCustomSectionsContent(tabId);
    
    // Custom sections content trumps everything - if there are custom sections with content, tab is publishable
    if (hasCustomSections) {
      return {
        hasContent: true,
        mode: 'custom-sections',
        reason: 'Custom sections have content'
      };
    }

    // Check content based on current mode
    let modeContent = false;
    let modeType = '';

    if (isTabModeCustom) {
      // Tab Mode Custom - check tab mode content field
      const tabModeContentField = `tabMode${tabId.charAt(0).toUpperCase() + tabId.slice(1)}Content`;
      modeContent = hasContent(data[tabModeContentField]);
      modeType = 'tab-mode-custom';
    } else {
      // Default mode - check default content fields
      modeContent = checkDefaultModeContent(tabId);
      modeType = 'default';
    }

    return {
      hasContent: modeContent,
      mode: modeType,
      reason: modeContent ? `${modeType} mode has content` : `${modeType} mode has no content`
    };
  };

  // Helper function to check default mode content for each tab
  const checkDefaultModeContent = (tabId) => {
    switch (tabId) {
      case 'home':
        return HomeTabPreview.hasContent(data);
      
      case 'location':
        return hasContent(data.venueName) || 
               hasContent(data.venueAddress) || 
               hasContent(data.parkingInfo) || 
               hasContent(data.publicTransit) || 
               hasContent(data.nearbyHotels) || 
               hasContent(data.diningOptions);
      
      case 'contactInfo':
        return hasContent(data.directorName) || 
               hasContent(data.directorEmail) || 
               hasContent(data.directorPhone) || 
               hasContent(data.organizerName) || 
               hasContent(data.organizerEmail) || 
               hasContent(data.organizerPhone) || 
               hasContent(data.registrationName) || 
               hasContent(data.registrationEmail) || 
               hasContent(data.registrationPhone) || 
               hasContent(data.websiteUrl);
      
      case 'rules':
        return hasContent(data.fideRules) || 
               hasContent(data.uscfRules) || 
               hasContent(data.tiebreakRules) || 
               hasContent(data.ratingRequirements) || 
               hasContent(data.deviceRules) || 
               hasContent(data.pairingRules) || 
               hasContent(data.additionalRules);
      
      case 'registration':
        // Registration tab doesn't require content - it can be live without any content
        return true;
      
      case 'faq':
        return hasContent(data.faqQuestion1) || 
               hasContent(data.faqQuestion2) || 
               hasContent(data.faqQuestion3) || 
               hasContent(data.faqQuestion4) || 
               hasContent(data.faqQuestion5) || 
               hasContent(data.faqQuestion6) || 
               hasContent(data.faqAnswer1) || 
               hasContent(data.faqAnswer2) || 
               hasContent(data.faqAnswer3) || 
               hasContent(data.faqAnswer4) || 
               hasContent(data.faqAnswer5) || 
               hasContent(data.faqAnswer6);
      
      case 'results':
        return hasContent(data.resultsLink);
      
      case 'participants':
      return hasParticipants(data.participants);
      
      case 'schedule':
        return hasContent(data.scheduleLink);
      
      case 'about':
        return hasContent(data.eventDescription);
      
      case 'custom':
        return hasCustomTabs(data.customTabs);
      
      default:
        return false;
    }
  };

  // Tab content checkers - only show tabs that have actual content (using rigid validation)
  const tabContentCheckers = {
    home: () => getTabContentStatus('home').hasContent,
    location: () => getTabContentStatus('location').hasContent,
    contactInfo: () => getTabContentStatus('contactInfo').hasContent,
    rules: () => getTabContentStatus('rules').hasContent,
    custom: () => getTabContentStatus('custom').hasContent,
    registration: () => getTabContentStatus('registration').hasContent,
    faq: () => getTabContentStatus('faq').hasContent,
    results: () => getTabContentStatus('results').hasContent,
    participants: () => {
      const participantsObj = data.participants || {};
      const participantsArr = Array.isArray(participantsObj)
        ? participantsObj
        : Object.values(participantsObj || {});
      return toggles.showParticipants && participantsArr.length > 0;
    },
    schedule: () => {
      const hasRounds = Array.isArray(data.rounds) && data.rounds.length > 0;
      const hasScheduleLink = typeof data.scheduleLink === 'string' && data.scheduleLink.trim() !== '';
      const hasScheduleInfo = typeof data.scheduleInfo === 'string' && data.scheduleInfo.trim() !== '';
      const hasScheduleInstructions = typeof data.scheduleInstructions === 'string' && data.scheduleInstructions.trim() !== '';
      return toggles.showSchedule && (hasRounds || hasScheduleLink || hasScheduleInfo || hasScheduleInstructions);
    },
    about: () => getTabContentStatus('about').hasContent
  };

  // Get custom tabs that should be shown
  const getCustomTabs = () => {
    if (!data.customTabs || !Array.isArray(data.customTabs)) return [];
    
    // Only show custom tabs if the main custom tab is enabled
    if (toggles.showCustom === false) return [];
    
    return data.customTabs
      .filter(tab => hasContent(tab.label) || hasContent(tab.content))
      .map((tab, index) => ({
        id: `custom-${tab.id || index}`,
        label: tab.label || `Custom Tab ${index + 1}`,
        toggleKey: 'showCustom', // All custom tabs use the same toggle
        isCustom: true,
        tabData: tab
      }));
  };

  // Get standard tabs that should be shown (have content AND are toggled on)
  const getStandardTabs = () => {
    const standardTabs = [
      { id: 'home', label: 'Home', toggleKey: 'showHome' },
      { id: 'about', label: 'About', toggleKey: 'showAbout' },

      { id: 'location', label: 'Location', toggleKey: 'showLocation' },
      { id: 'contactInfo', label: 'Contact', toggleKey: 'showContactInfo' },
      { id: 'rules', label: 'Rulebook', toggleKey: 'showRules' },
      { id: 'faq', label: 'FAQ', toggleKey: 'showFAQ' },
      { id: 'participants', label: 'Participants', toggleKey: 'showParticipants' },

      { id: 'schedule', label: 'Schedule', toggleKey: 'showSchedule' },
      { id: 'results', label: 'Results', toggleKey: 'showResults' },

    ];

    return standardTabs.filter(tab => 
      toggles[tab.toggleKey] && tabContentCheckers[tab.id]()
    );
  };

  // Get registration tab separately to ensure it's always last
  const getRegistrationTab = () => {
    const registrationTab = { id: 'registration', label: 'Registration', toggleKey: 'showRegistration' };
    return (toggles[registrationTab.toggleKey] && tabContentCheckers[registrationTab.id]()) ? [registrationTab] : [];
  };

  // Combine standard tabs, custom tabs, and registration tab (registration always last)
  const visibleTabs = [...getStandardTabs(), ...getCustomTabs(), ...getRegistrationTab()];

  // If no tabs have content, show a message
  if (visibleTabs.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="bg-chess-cream rounded-lg p-8">
          <h3 className="text-lg font-semibold text-gray-600 mb-2">
            No Content to Preview
          </h3>
          <p className="text-gray-500">
            Fill in the form fields to see your tournament page preview here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" style={{
        paddingBottom: '20px',
      }}>
      
      {/* Event Title & Description Header - Always at top when Home has content */}
      {hasContent(data.eventTitle) && (
        <div
          className="relative text-center py-12 px-6 sm:py-16 sm:px-8 md:py-20 md:px-12"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '5px'
          }}
        >
          {hasContent(data.eventLogo) && (
            <div className="flex justify-center mb-4">
              <img
                src={data.eventLogo}
                alt={`${data.eventTitle || 'Tournament'} logo`}
                className="h-20 sm:h-24 md:h-28 w-auto max-w-xs object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          )}
          <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold mb-4 leading-snug"
            style={{ 
              color: data.primaryColorHex || '#3B82F6'
            }}>
            {data.eventTitle}
          </h1>

          {/* Elegant Divider */}
          <div className="mb-8 flex items-center justify-center space-x-6">
            <div className="w-24 h-0.5"
              style={{ background: `linear-gradient(to right, transparent, ${data.primaryColorHex || '#3B82F6'})` }}></div>
            <div className="w-4 h-4 rounded-full"
              style={{ backgroundColor: data.primaryColorHex || '#3B82F6' }}></div>
            <div className="w-24 h-0.5"
              style={{ background: `linear-gradient(to left, transparent, ${data.primaryColorHex || '#3B82F6'})` }}></div>
          </div>

          {/* Accent Bar */}
          {/* <div
            className="mx-auto mt-2 mb-4"
            style={{
              width: 80,
              height: 6,
              borderRadius: 3,
              background: data.primaryColorHex || '#3B82F6',
              boxShadow: '0 0 10px rgba(0,0,0,0.5)'
            }}
          /> */}

          {/* Date/Location/Announcement Badges */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4 px-4">
            {/* Announcement Badge */}
            {data.announcement && typeof data.announcement === 'string' && data.announcement.trim() !== '' && (
              <div className="bg-transparent backdrop-blur-sm text-yellow-400 font-medium px-4 py-2 sm:px-6 sm:py-2 rounded-full text-sm sm:text-base border-2 border-yellow-400 opacity-90">
                {data.announcement}
              </div>
            )}
            {/* Date Badge - Recurring or One-time */}
            {(isRecurringEvent(data) || hasContent(data.startDate)) && (
              <div className="bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 sm:px-6 sm:py-2 border border-white/30">
                <span className="text-xs sm:text-sm font-medium text-white">
                  {(() => {
                    // Handle recurring events
                    if (isRecurringEvent(data)) {
                      const nextDate = calculateNextOccurrence(data);
                      if (nextDate) {
                        const dateStr = nextDate.toLocaleDateString('en-US', {
                          weekday: window.innerWidth < 640 ? 'short' : 'long',
                          year: 'numeric',
                          month: window.innerWidth < 640 ? 'short' : 'long',
                          day: 'numeric'
                        });
                        return `🔁 Next: ${dateStr}`;
                      } else {
                        return `🔁 ${formatRecurrencePattern(data)}`;
                      }
                    }

                    // Handle one-time events
                    if (!data.startDate) return '';
                    // Parse date as UTC to avoid timezone issues
                    const match = data.startDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
                    if (!match) return data.startDate;
                    const year = parseInt(match[1], 10);
                    const month = parseInt(match[2], 10) - 1; // JS months are 0-based
                    const day = parseInt(match[3], 10);
                    const utcDate = new Date(Date.UTC(year, month, day));
                    return `📅 ${utcDate.toLocaleDateString('en-US', {
                      weekday: window.innerWidth < 640 ? 'short' : 'long',
                      year: 'numeric',
                      month: window.innerWidth < 640 ? 'short' : 'long',
                      day: 'numeric',
                      timeZone: 'UTC',
                    })}`;
                  })()}
                </span>
              </div>
            )}
            {(hasContent(data.venueName) || hasContent(data.city)) && (
              <div className="bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 sm:px-6 sm:py-2 border border-white/30">
                <span className="text-xs sm:text-sm font-medium text-white">
                  📍 {hasContent(data.city) ? data.city : data.venueName}
                </span>
              </div>
            )}
          </div>
          
          {/* Register Button below date/location info */}
          {toggles.showRegistration && (() => {
            const useInternal = data.useInternalRegistration !== false; // default to true
            const hasExternalLink = data.registrationLink && typeof data.registrationLink === 'string' && data.registrationLink.trim() !== '';
            const shouldShowButton = useInternal || hasExternalLink;
            
            if (!shouldShowButton) {
              return null; // Don't render button if no registration method available
            }
            
            let buttonHref = `/register?eventId=${data.eventId || data.id || ''}`;
            let buttonTarget = undefined;
            let buttonRel = undefined;
            if (!useInternal && hasExternalLink) {
              let link = typeof data.registrationLink === 'string' ? data.registrationLink.trim() : '';
              if (!/^https?:\/\//i.test(link)) {
                link = 'https://' + link;
              }
              buttonHref = link;
              buttonTarget = '_blank';
              buttonRel = 'noopener noreferrer';
            }
            return (
            <div className="flex justify-center mt-8 mb-6">
              <a
                  href={buttonHref}
                  target={buttonTarget}
                  rel={buttonRel}
                className="inline-block text-white font-bold px-10 py-3 rounded-full shadow-2xl transform hover:scale-105 transition-all duration-300 text-lg border-2 border-white/20"
                style={{
                  background: `linear-gradient(to right, ${data.primaryColorHex || '#3B82F6'}, ${data.primaryColorHex || '#3B82F6'}90)`,
                  boxShadow: `0 8px 32px ${data.primaryColorHex || '#3B82F6'}40`
                }}
                onMouseEnter={(e) => {
                  const color = data.primaryColorHex || '#3B82F6';
                  e.target.style.background = `linear-gradient(to right, ${darkenColor(color, 0.2)}, ${color})`;
                }}
                onMouseLeave={(e) => {
                  const color = data.primaryColorHex || '#3B82F6';
                  e.target.style.background = `linear-gradient(to right, ${color}, ${color}90)`;
                }}
              >
                Register Now <i className="fa-solid fa-chess-king ml-1"></i>
              </a>
            </div>
            );
          })()}
        </div>
      )}

      {/* Tab Navigation */}
      {visibleTabs.length > 1 && (
        <div className="relative">
          {/* Desktop Navigation - Hidden on mobile */}
          <div className="tab-nav-desktop w-full overflow-x-auto">
            <div className="flex justify-center min-w-full">
              <nav className="flex space-x-2 p-3 rounded-lg mx-auto bg-chess-cream"
              style={{
                maxWidth: 'fit-content', 
                border: '2px solid #f7f4eb',
                minWidth: 'max-content'
              }}>
                {visibleTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => onTabChange(tab.id)}
                    className={`py-3 px-4 border-2 font-semibold text-base sm:text-base transition-all rounded-lg duration-200 whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'text-white rounded-lg'
                        : 'text-black border-transparent button-hover-primary'
                    }`}
                    style={
                      activeTab === tab.id
                        ? {
                            background: `linear-gradient(to right, ${data.primaryColorHex || '#3B82F6'}, ${data.primaryColorHex || '#3B82F6'}90)`,
                            border: `2px solid ${data.primaryColorHex || '#3B82F6'}`,
                          }
                        : {
                            '--primary': data.primaryColorHex || '#3B82F6',
                            border: '2px solid transparent',
                          }
                    }
                  >
                      <span className="capitalize">{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Mobile Navigation - Menu button with dropdown */}
          <div className="tab-nav-mobile justify-center">
            <div className="relative">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="flex items-center gap-2 px-4 py-2 text-black font-semibold text-sm rounded-lg transition-all"
                style={{ 
                  background: '#f7f4eb',
                  border: `2px solid ${data.primaryColorHex || '#3B82F6'}`
                }}
              >
                Menu
                <svg 
                  className={`w-4 h-4 transition-transform duration-200 ${isMobileMenuOpen ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Mobile Dropdown Menu */}
              {isMobileMenuOpen && (
                <>
                  {/* Backdrop */}
                  <div 
                    className="fixed inset-0 z-40 bg-black bg-opacity-25" 
                    onClick={() => setIsMobileMenuOpen(false)}
                  />
                  
                  {/* Dropdown */}
                  <div 
                    className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-48 rounded-lg shadow-lg z-50 overflow-hidden"
                    style={{ 
                      background: 'rgba(0, 0, 0, 0.9)',
                      border: `2px solid ${data.primaryColorHex || '#3B82F6'}`
                    }}
                  >
                    {visibleTabs.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => {
                          onTabChange(tab.id);
                          setIsMobileMenuOpen(false);
                        }}
                        className={`w-full text-left px-4 py-3 text-white font-medium transition-colors hover:bg-black hover:bg-opacity-20 ${
                          activeTab === tab.id ? 'bg-black bg-opacity-30' : ''
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tab Content */}
      <div>
        
        {/* Home Tab - Using enhanced component */}
        {activeTab === 'home' && toggles.showHome && (
          <div className="space-y-2">
            <HomeTabPreview data={data} showRegistration={toggles.showRegistration} onTabChange={onTabChange} />
            <CustomSectionsRenderer 
              sections={data.homeCustomSections} 
              primaryColorHex={data.primaryColorHex} 
            />
            {/* Render 'Other' content at the very end of the Home page preview */}
            {hasContent(data.otherContent) && (
              <div className="p-4 sm:p-6 md:p-8">
                <div className="bg-chess-cream rounded-xl border border-gray-200 shadow-sm p-6 sm:p-8 mb-8 sm:mb-12 mt-8 sm:mt-12 max-w-full sm:max-w-3xl mx-auto">
                  <h2 className="text-lg sm:text-xl font-semibold text-center mb-4 sm:mb-6 text-gray-900">Other</h2>
                  <div className="text-base sm:text-lg font-semibold text-gray-900 leading-relaxed">
                    {data.otherContent}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Location Tab */}
        {activeTab === 'location' && toggles.showLocation && tabContentCheckers.location() && (
          <div className="max-w-4xl mx-auto overflow-hidden">
            <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
              
              {/* Custom Mode Content */}
              {data.tabCustomModes?.location && hasContent(data.tabModeLocationContent) ? (
                <div className="bg-chess-cream" style={{
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
                  padding: '24px',
                  marginBottom: '24px',
                  maxWidth: '850px',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  textAlign: 'center'
                }}>
                  {renderSimpleFormattedText(data.tabModeLocationContent)}
                </div>
              ) : (
                <>
              {/* Venue Information */}
              {(hasContent(data.venueName) || hasContent(data.venueAddress)) && (
                <div className="bg-chess-cream" style={{
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
                  padding: '24px',
                  marginBottom: '24px',
                  maxWidth: '850px',
                  marginLeft: 'auto',
                  marginRight: 'auto'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    textAlign: 'center',
                    marginBottom: '16px',
                    color: '#111827'
                  }}>Location</h3>
                  {hasContent(data.venueName) && (
                    <p style={{
                      fontSize: '16px',
                      color: '#111827',
                      textAlign: 'center'
                    }}><strong>Venue:</strong> {data.venueName}</p>
                  )}
                  {hasContent(data.venueAddress) && (
                    <p style={{
                      fontSize: '16px',
                      color: '#111827',
                      textAlign: 'center'
                    }}> <strong>Address:</strong> {data.venueAddress}</p>
                  )}
                  {hasContent(data.parkingInfo) && (
                    <p style={{
                      fontSize: '16px',
                      color: '#111827',
                      textAlign: 'center'
                    }}><strong>Parking:</strong> {data.parkingInfo}</p>
                  )}
                  {hasContent(data.publicTransit) && (
                    <p style={{
                      fontSize: '16px',
                      color: '#111827',
                      textAlign: 'center'
                    }}><strong>Venue Entrance:</strong> {data.publicTransit}</p>
                  )}
                </div>
              )}

              

              {/* Accommodations */}
              {(hasContent(data.nearbyHotels) || hasContent(data.diningOptions)) && (
                <div className="bg-chess-cream" style={{
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
                  padding: '24px',
                  marginBottom: '24px',
                  maxWidth: '850px',
                  marginLeft: 'auto',
                  marginRight: 'auto'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    textAlign: 'center',
                    marginBottom: '16px',
                    color: '#111827'
                  }}>Accommodations</h3>
                  {hasContent(data.nearbyHotels) && (
                    <p style={{
                      fontSize: '16px',
                      color: '#111827',
                      textAlign: 'center'
                    }}><strong>Hotels:</strong> {data.nearbyHotels}</p>
                  )}
                  {hasContent(data.diningOptions) && (
                    <p style={{
                      fontSize: '16px',
                      color: '#111827',
                      textAlign: 'center'
                    }}><strong>Dining:</strong> {data.diningOptions}</p>
                  )}
                </div>
              )}

              {/* Transportation
              {(hasContent(data.parkingInfo) || hasContent(data.publicTransit)) && (
                <div className="bg-gray-50 rounded-lg p-6" style={{ borderBottom: `4px solid ${data.primaryColorHex || '#3B82F6'}`, borderLeft: `4px solid ${data.primaryColorHex || '#3B82F6'}` }}>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">Transportation</h3>
                  
                </div>
              )} */}
                </>
              )}
              
              {/* Custom Sections */}
              <CustomSectionsRenderer 
                sections={data.locationCustomSections || []} 
                primaryColorHex={data.primaryColorHex} 
              />
            </div>
          </div>
        )}

        {/* Contact Info Tab */}
        {activeTab === 'contactInfo' && toggles.showContactInfo && tabContentCheckers.contactInfo() && (
          <div className="max-w-4xl mx-auto overflow-hidden">
            <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
              
              {/* Custom Mode Content */}
              {data.tabCustomModes?.contactInfo && hasContent(data.tabModeContactContent) ? (
                <div className="bg-chess-cream" style={{
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
                  padding: '24px',
                  marginBottom: '24px',
                  maxWidth: '850px',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  textAlign: 'center'
                }}>
                  {renderSimpleFormattedText(data.tabModeContactContent)}
                </div>
              ) : (
                <>
              {/* Tournament Director */}
              {(hasContent(data.directorName) || hasContent(data.directorEmail) || hasContent(data.directorPhone)) && (
                <div className="bg-chess-cream" style={{
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
                  padding: '24px',
                  marginBottom: '24px',
                  maxWidth: '850px',
                  marginLeft: 'auto',
                  marginRight: 'auto'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    textAlign: 'center',
                    marginBottom: '16px',
                    color: '#111827'
                  }}>Tournament Director</h3>
                  {hasContent(data.directorName) && (
                        <p style={{
                          fontSize: '16px',
                          color: '#111827',
                          textAlign: 'center'
                        }}>{data.directorName}</p>
                  )}
                  {hasContent(data.directorEmail) && (
                        <p style={{
                          fontSize: '16px',
                          color: '#111827',
                          textAlign: 'center'
                        }}>
                            {data.directorEmail}
                        </p>
                  )}
                  {hasContent(data.directorPhone) && (
                        <p style={{
                          fontSize: '16px',
                          color: '#111827',
                          textAlign: 'center'
                        }}>
                            {data.directorPhone}
                        </p>
                  )}
                </div>
              )}

              {/* Organizer */}
              {(hasContent(data.organizerName) || hasContent(data.organizerEmail) || hasContent(data.organizerPhone)) && (
                <div className="bg-chess-cream" style={{
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
                  padding: '24px',
                  marginBottom: '24px',
                  maxWidth: '850px',
                  marginLeft: 'auto',
                  marginRight: 'auto'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    textAlign: 'center',
                    marginBottom: '16px',
                    color: '#111827'
                  }}>Organizer</h3>
                  {hasContent(data.organizerName) && (
                        <p style={{
                          fontSize: '16px',
                          color: '#111827',
                          textAlign: 'center'
                        }}>{data.organizerName}</p>
                  )}
                  {hasContent(data.organizerEmail) && (
                        <p style={{
                          fontSize: '16px',
                          color: '#111827',
                          textAlign: 'center'
                        }}>
                            {data.organizerEmail}
                        </p>
                  )}
                  {hasContent(data.organizerPhone) && (
                        <p style={{
                          fontSize: '16px',
                          color: '#111827',
                          textAlign: 'center'
                        }}>
                            {data.organizerPhone}
                        </p>
                  )}
                </div>
              )}

              {/* Registration Contact */}
              {(hasContent(data.registrationName) || hasContent(data.registrationEmail) || hasContent(data.registrationPhone)) && (
                <div className="bg-chess-cream" style={{
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
                  padding: '24px',
                  marginBottom: '24px',
                  maxWidth: '850px',
                  marginLeft: 'auto',
                  marginRight: 'auto'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    textAlign: 'center',
                    marginBottom: '16px',
                    color: '#111827'
                  }}>Registration</h3>
                  {hasContent(data.registrationName) && (
                        <p style={{
                          fontSize: '16px',
                          color: '#111827',
                          textAlign: 'center'
                        }}>{data.registrationName}</p>
                  )}
                  {hasContent(data.registrationEmail) && (
                        <p style={{
                          fontSize: '16px',
                          color: '#111827',
                          textAlign: 'center'
                        }}>
                            {data.registrationEmail}
                        </p>
                  )}
                  {hasContent(data.registrationPhone) && (
                        <p style={{
                          fontSize: '16px',
                          color: '#111827',
                          textAlign: 'center'
                        }}>
                            {data.registrationPhone}
                        </p>
                  )}
                </div>
              )}

              {/* Website */}
              {hasContent(data.websiteUrl) && (() => {
                let websiteUrl = data.websiteUrl.trim();
                if (!/^https?:\/\//i.test(websiteUrl)) {
                  websiteUrl = 'https://' + websiteUrl;
                }
                return (
                  <div style={{
                                borderRadius: '12px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
                    padding: '24px',
                    marginBottom: '24px',
                    maxWidth: '850px',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    background: '#f5f5dc'
                  }}>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: 600,
                      textAlign: 'center',
                      marginBottom: '16px',
                      color: '#111827'
                    }}>Website</h3>
                    <p style={{
                      fontSize: '16px',
                      color: '#111827',
                      textAlign: 'center'
                    }}>
                      <a href={websiteUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                        {data.websiteUrl}
                      </a>
                    </p>
                  </div>
                );
              })()}
                </>
              )}
              
              {/* Custom Sections */}
              <CustomSectionsRenderer 
                sections={data.contactInfoCustomSections} 
                primaryColorHex={data.primaryColorHex} 
              />
            </div>
          </div>
        )}

        {/* Rules & Format Tab */}
        {activeTab === 'rules' && toggles.showRules && tabContentCheckers.rules() && (
          <div className="max-w-4xl mx-auto overflow-hidden">
            <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
              
              {/* Custom Mode Content */}
              {data.tabCustomModes?.rules && hasContent(data.tabModeRulesContent) ? (
                <div className="bg-chess-cream" style={{
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
                  padding: '24px',
                  marginBottom: '24px',
                  maxWidth: '850px',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  textAlign: 'center'
                }}>
                  {renderSimpleFormattedText(data.tabModeRulesContent)}
                </div>
              ) : (
                <>
                  {hasContent(data.fideRules) && (
                    <div className="bg-chess-cream" style={{
                                    borderRadius: '12px',
                      border: '1px solid #E5E7EB',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
                      padding: '24px',
                      marginBottom: '24px',
                      maxWidth: '850px',
                      marginLeft: 'auto',
                      marginRight: 'auto'
                    }}>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: 600,
                        textAlign: 'center',
                        marginBottom: '16px',
                        color: '#111827'
                      }}>FIDE Rules</h3>
                      <div style={{
                        fontSize: '16px',
                        color: '#111827',
                        textAlign: 'center'
                      }}>{renderSimpleFormattedText(data.fideRules)}</div>
                    </div>
                  )}

                  {hasContent(data.uscfRules) && (
                    <div className="bg-chess-cream" style={{
                                    borderRadius: '12px',
                      border: '1px solid #E5E7EB',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
                      padding: '24px',
                      marginBottom: '24px',
                      maxWidth: '850px',
                      marginLeft: 'auto',
                      marginRight: 'auto'
                    }}>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: 600,
                        textAlign: 'center',
                        marginBottom: '16px',
                        color: '#111827'
                      }}>USCF Rules</h3>
                      <div style={{
                        fontSize: '16px',
                        color: '#111827',
                        textAlign: 'center'
                      }}>{renderSimpleFormattedText(data.uscfRules)}</div>
                    </div>
                  )}

              {hasContent(data.tiebreakRules) && (
                <div className="bg-chess-cream" style={{
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
                  padding: '24px',
                  marginBottom: '24px',
                  maxWidth: '850px',
                  marginLeft: 'auto',
                  marginRight: 'auto'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    textAlign: 'center',
                    marginBottom: '16px',
                    color: '#111827'
                  }}>Tiebreak Rules</h3>
                  <div style={{
                    fontSize: '16px',
                    color: '#111827',
                    textAlign: 'center'
                  }}>{renderSimpleFormattedText(data.tiebreakRules)}</div>
                </div>
              )}

                  {hasContent(data.ratingRequirements) && (
                    <div className="bg-chess-cream" style={{
                                    borderRadius: '12px',
                      border: '1px solid #E5E7EB',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
                      padding: '24px',
                      marginBottom: '24px',
                      maxWidth: '850px',
                      marginLeft: 'auto',
                      marginRight: 'auto'
                    }}>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: 600,
                        textAlign: 'center',
                        marginBottom: '16px',
                        color: '#111827'
                      }}>Rating Requirements</h3>
                      <div style={{
                        fontSize: '16px',
                        color: '#111827',
                        textAlign: 'center'
                      }}>{renderSimpleFormattedText(data.ratingRequirements)}</div>
                    </div>
                  )}

                  {hasContent(data.deviceRules) && (
                    <div className="bg-chess-cream" style={{
                                    borderRadius: '12px',
                      border: '1px solid #E5E7EB',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
                      padding: '24px',
                      marginBottom: '24px',
                      maxWidth: '850px',
                      marginLeft: 'auto',
                      marginRight: 'auto'
                    }}>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: 600,
                        textAlign: 'center',
                        marginBottom: '16px',
                        color: '#111827'
                      }}>Device Rules</h3>
                      <div style={{
                        fontSize: '16px',
                        color: '#111827',
                        textAlign: 'center'
                      }}>{renderSimpleFormattedText(data.deviceRules)}</div>
                    </div>
                  )}

                  {hasContent(data.pairingRules) && (
                    <div className="bg-chess-cream" style={{
                                    borderRadius: '12px',
                      border: '1px solid #E5E7EB',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
                      padding: '24px',
                      marginBottom: '24px',
                      maxWidth: '850px',
                      marginLeft: 'auto',
                      marginRight: 'auto'
                    }}>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: 600,
                        textAlign: 'center',
                        marginBottom: '16px',
                        color: '#111827'
                      }}>Pairing Rules</h3>
                      <div style={{
                        fontSize: '16px',
                        color: '#111827',
                        textAlign: 'center'
                      }}>{renderSimpleFormattedText(data.pairingRules)}</div>
                    </div>
                  )}

              {hasContent(data.additionalRules) && (
                <div className="bg-chess-cream" style={{
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
                  padding: '24px',
                  marginBottom: '24px',
                  maxWidth: '850px',
                  marginLeft: 'auto',
                  marginRight: 'auto'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    textAlign: 'center',
                    marginBottom: '16px',
                    color: '#111827'
                  }}>Additional Rules</h3>
                  <div style={{
                    fontSize: '16px',
                    color: '#111827',
                    textAlign: 'center'
                  }}>{renderSimpleFormattedText(data.additionalRules)}</div>
                </div>
              )}
                </>
              )}
              
              {/* Custom Sections */}
              <CustomSectionsRenderer 
                sections={data.rulesCustomSections} 
                primaryColorHex={data.primaryColorHex} 
              />
            </div>
          </div>
        )}

        {/* Custom Tabs - Dynamic rendering for each custom tab */}
        {activeTab.startsWith('custom-') && toggles.showCustom && (() => {
          const customTabId = activeTab.replace('custom-', '');
          const customTab = data.customTabs?.find(tab => (tab.id || '').toString() === customTabId);
          
          if (!customTab) return null;
          
          return (
            <div className="max-w-4xl mx-auto overflow-hidden">
              <div className="p-8 space-y-8">
                <div className="bg-chess-cream" style={{
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
                  padding: '24px',
                  marginBottom: '24px',
                  maxWidth: '850px',
                  marginLeft: 'auto',
                  marginRight: 'auto'
                }}>
                  <h2 style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    textAlign: 'center',
                    marginBottom: '16px',
                    color: '#111827'
                  }}>
                    {customTab.label || 'Custom Section'}
                  </h2>
                  {customTab.content && (
                    <div style={{
                      fontSize: '16px',
                      color: '#111827',
                      textAlign: 'center'
                    }}>
                      {renderSimpleFormattedText(customTab.content)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Registration Tab */}
        {activeTab === 'registration' && toggles.showRegistration && tabContentCheckers.registration() && (
          <div className="max-w-4xl mx-auto overflow-hidden">
            <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
              <div className="bg-chess-cream" style={{
                borderRadius: '12px',
                border: '1px solid #E5E7EB',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
                padding: '24px',
                marginBottom: '24px',
                maxWidth: '850px',
                marginLeft: 'auto',
                marginRight: 'auto'
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  textAlign: 'center',
                  marginBottom: '16px',
                  color: '#111827'
                }}>Registration</h3>
                
                {/* Registration Instructions */}
                <div style={{
                  fontSize: '16px',
                  color: '#111827',
                  textAlign: 'center',
                  marginBottom: '20px'
                }}>
                  {hasContent(data.registrationInstructions) 
                    ? renderSimpleFormattedText(data.registrationInstructions) 
                    : 'Click the button below to register for the tournament'
                  }
                </div>

                {/* Register Now Button */}
                <div style={{ textAlign: 'center' }}>
                  {(() => {
                    const useInternal = data.useInternalRegistration !== false; // default to true
                    const hasExternalLink = data.registrationLink && typeof data.registrationLink === 'string' && data.registrationLink.trim() !== '';
                    const shouldShowButton = useInternal || hasExternalLink;
                    
                    if (!shouldShowButton) {
                      return <p style={{
                        fontSize: '16px',
                        color: '#111827'
                      }}>Registration opening soon.</p>;
                    }
                    
                    let buttonHref = `/register?eventId=${data.eventId || data.id || ''}`;
                    let buttonTarget = undefined;
                    let buttonRel = undefined;
                    if (!useInternal && hasExternalLink) {
                      let link = typeof data.registrationLink === 'string' ? data.registrationLink.trim() : '';
                      if (!/^https?:\/\//i.test(link)) {
                        link = 'https://' + link;
                      }
                      buttonHref = link;
                      buttonTarget = '_blank';
                      buttonRel = 'noopener noreferrer';
                    }
                    return (
                      <a
                        href={buttonHref}
                        target={buttonTarget}
                        rel={buttonRel}
                        style={{
                          backgroundColor: data.primaryColorHex || '#3B82F6',
                          color: 'white',
                          fontWeight: 600,
                          padding: '12px 24px',
                          fontSize: '16px',
                          borderRadius: '8px',
                          boxShadow: '0 8px 32px 0 rgba(255, 215, 0, 0.4), 0 0 20px rgba(255, 215, 0, 0.3)',
                          cursor: 'pointer',
                          textDecoration: 'none',
                          display: 'inline-block',
                          position: 'relative',
                          zIndex: 1,
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          const color = data.primaryColorHex || '#3B82F6';
                          // Darken the color slightly on hover
                          const hexToRgb = (hex) => {
                            const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
                            return result ? {
                              r: parseInt(result[1], 16),
                              g: parseInt(result[2], 16),
                              b: parseInt(result[3], 16)
                            } : null;
                          };
                          const rgb = hexToRgb(color);
                          if (rgb) {
                            const darkenAmount = 0.1;
                            const r = Math.max(0, rgb.r - Math.round(255 * darkenAmount));
                            const g = Math.max(0, rgb.g - Math.round(255 * darkenAmount));
                            const b = Math.max(0, rgb.b - Math.round(255 * darkenAmount));
                            e.target.style.backgroundColor = `rgb(${r}, ${g}, ${b})`;
                          }
                          e.target.style.boxShadow = '0 8px 32px 0 rgba(255, 215, 0, 0.6), 0 0 30px rgba(255, 215, 0, 0.5)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = data.primaryColorHex || '#3B82F6';
                          e.target.style.boxShadow = '0 8px 32px 0 rgba(255, 215, 0, 0.4), 0 0 20px rgba(255, 215, 0, 0.3)';
                        }}
                      >
                        Register Now <i className="fa-solid fa-chess-king ml-1"></i>
                      </a>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* FAQ Tab */}
        {activeTab === 'faq' && toggles.showFAQ && tabContentCheckers.faq() && (
          <div className="max-w-4xl mx-auto overflow-hidden">
            <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
              
              {/* Custom Mode Content */}
              {data.tabCustomModes?.faq && hasContent(data.tabModeFAQContent) ? (
                <div className="bg-chess-cream" style={{
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
                  padding: '24px',
                  marginBottom: '24px',
                  maxWidth: '850px',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  textAlign: 'center'
                }}>
                  {renderSimpleFormattedText(data.tabModeFAQContent)}
                </div>
              ) : (
                <>
                {/* FAQ Items */}
                  {[
                    { q: data.faqQuestion1, a: data.faqAnswer1 },
                    { q: data.faqQuestion2, a: data.faqAnswer2 },
                    { q: data.faqQuestion3, a: data.faqAnswer3 },
                    { q: data.faqQuestion4, a: data.faqAnswer4 },
                    { q: data.faqQuestion5, a: data.faqAnswer5 },
                    { q: data.faqQuestion6, a: data.faqAnswer6 }
                  ].map((faq, index) => (
                    hasContent(faq.q) && hasContent(faq.a) && (
                      <div key={index} className="bg-chess-cream" style={{
                        borderRadius: '12px',
                        border: '1px solid #E5E7EB',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
                        padding: '24px',
                        marginBottom: '24px',
                        maxWidth: '850px',
                        marginLeft: 'auto',
                        marginRight: 'auto'
                      }}>
                        <h3 style={{
                          fontSize: '18px',
                          fontWeight: 600,
                          textAlign: 'center',
                          marginBottom: '16px',
                          color: '#111827'
                        }}>{faq.q}</h3>
                        <div style={{
                          fontSize: '16px',
                          color: '#111827',
                          textAlign: 'center'
                        }}>{faq.a}</div>
                      </div>
                    )
                  ))}
                </>
              )}
              
              {/* Custom Sections */}
              <CustomSectionsRenderer 
                sections={data.faqCustomSections} 
                primaryColorHex={data.primaryColorHex} 
              />
            </div>
          </div>
        )}

        {/* Results Tab */}
        {activeTab === 'results' && toggles.showResults && tabContentCheckers.results() && (
          <div style={{
            textAlign: 'center',
            marginTop: '32px',
            marginBottom: '32px'
          }}>
            {hasContent(data.resultsLink) && (() => {
              let link = data.resultsLink.trim();
              if (!/^https?:\/\//i.test(link)) {
                link = 'https://' + link;
              }
              return (
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  backgroundColor: data.primaryColorHex || '#3B82F6',
                  color: 'white',
                  fontWeight: 600,
                  padding: '12px 24px',
                  fontSize: '16px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  display: 'inline-block'
                }}
              >
                View Results <i className="fa-solid fa-arrow-up-right-from-square ml-1"></i>
              </a>
              );
            })()}
          </div>
        )}

        {/* Participants Tab */}
        {activeTab === 'participants' && (() => {
          const participantsObj = data.participants || {};
          const participantsArr = Array.isArray(participantsObj)
            ? participantsObj
            : Object.values(participantsObj || {});

          const sectionOptions = getNormalizedSections(data);

          // Filter and sort participants
          const sorted = participantsArr
            .filter(p => {
              if (!p) return false;
              const status = (p.status || '').toLowerCase();
              return status === 'paid' || status === 'registered';
            })
            .sort((a, b) => {
              if (data?.federationType === 'Unrated') {
                return (a.fullName || '').localeCompare(b.fullName || '');
              }
              const aRating = getParticipantRatingValue(a);
              const bRating = getParticipantRatingValue(b);
              return bRating - aRating;
            });

          const federationIdHeaderLabel = getFederationIdHeaderLabel(sorted, data?.federationType);

          // Filter by search query
          const searchTerm = participantsSearchQuery.toLowerCase().trim();
          const filtered = searchTerm === ''
            ? sorted
            : sorted.filter(p => {
                const name = (p.fullName || p.displayName || '').toLowerCase();
                const team = (p.teamName || '').toLowerCase();
                const section = getParticipantSectionLabel(p, sectionOptions).toLowerCase();
                return name.includes(searchTerm) || team.includes(searchTerm) || section.includes(searchTerm);
              });

          return (
            <div className="bg-chess-cream" style={{
              borderRadius: '12px',
              border: '1px solid #E5E7EB',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
              padding: '24px',
              marginBottom: '24px',
              maxWidth: '850px',
              marginLeft: 'auto',
              marginRight: 'auto'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 600,
                textAlign: 'center',
                marginBottom: '16px',
                color: '#111827'
              }}>Participants</h3>

              {/* Search Bar */}
              <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
                <input
                  type="text"
                  placeholder="Search by name..."
                  value={participantsSearchQuery}
                  onChange={(e) => setParticipantsSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    maxWidth: '400px',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    backgroundColor: '#ffffff'
                  }}
                />
              </div>

              <div className="overflow-x-auto -mx-2 sm:mx-0">
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '400px' }}>
                  <thead>
                    <tr>
                      <th style={{
                        backgroundColor: '#f7f4eb',
                        fontWeight: '600',
                        padding: '8px 12px',
                        fontSize: '12px',
                        textAlign: 'left',
                        borderBottom: '1px solid #E5E7EB'
                      }}>#</th>
                      <th style={{
                        backgroundColor: '#f7f4eb',
                        fontWeight: '600',
                        padding: '8px 12px',
                        fontSize: '12px',
                        textAlign: 'left',
                        borderBottom: '1px solid #E5E7EB'
                      }}>Name</th>
                      {data.isTeamEvent && (
                        <th style={{
                          backgroundColor: '#f7f4eb',
                          fontWeight: '600',
                          padding: '8px 12px',
                          fontSize: '12px',
                          textAlign: 'left',
                          borderBottom: '1px solid #E5E7EB'
                        }}>School</th>
                      )}
                      <th style={{
                        backgroundColor: '#f7f4eb',
                        fontWeight: '600',
                        padding: '8px 12px',
                        fontSize: '12px',
                        textAlign: 'left',
                        borderBottom: '1px solid #E5E7EB'
                      }}>Section</th>
                      <th style={{
                        backgroundColor: '#f7f4eb',
                        fontWeight: '600',
                        padding: '8px 12px',
                        fontSize: '12px',
                        textAlign: 'left',
                        borderBottom: '1px solid #E5E7EB'
                      }}>{federationIdHeaderLabel}</th>
                      <th style={{
                        backgroundColor: '#f7f4eb',
                        fontWeight: '600',
                        padding: '8px 12px',
                        fontSize: '12px',
                        textAlign: 'left',
                        borderBottom: '1px solid #E5E7EB'
                      }}>Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      return filtered.map((p, idx) => {
                      const sectionLabel = getParticipantSectionLabel(p, sectionOptions);
                      const federationIdLabel = getParticipantFederationIdLabel(p);
                      const ratingLabel = getParticipantRatingLabel(p);
                      // Create a stable, unique key for each participant
                      const participantKey = `${p.userId || 'no-id'}-${idx}-${searchTerm}`;
                      return (
                        <tr key={participantKey}>
                          <td style={{
                            backgroundColor: '#f7f4eb',
                            padding: '8px 12px',
                            fontSize: '12px',
                            borderBottom: '1px solid #E5E7EB'
                          }}>{idx + 1}</td>
                          <td style={{
                            backgroundColor: '#f7f4eb',
                            padding: '8px 12px',
                            fontSize: '12px',
                            borderBottom: '1px solid #E5E7EB'
                          }}>
                            {filterBadWords(p.fullName || p.displayName || 'N/A')}

                          </td>
                          {data.isTeamEvent && (
                            <td style={{
                              backgroundColor: '#f7f4eb',
                              padding: '8px 12px',
                              fontSize: '12px',
                              borderBottom: '1px solid #E5E7EB'
                            }}>
                              {filterBadWords(p.teamName || 'No Team')}
                            </td>
                          )}
                          <td style={{
                            backgroundColor: '#f7f4eb',
                            padding: '8px 12px',
                            fontSize: '12px',
                            borderBottom: '1px solid #E5E7EB'
                          }}>
                            {sectionLabel}
                          </td>
                          <td style={{
                            backgroundColor: '#f7f4eb',
                            padding: '8px 12px',
                            fontSize: '12px',
                            borderBottom: '1px solid #E5E7EB'
                          }}>
                            {federationIdLabel}
                          </td>
                          <td style={{
                            backgroundColor: '#f7f4eb',
                            padding: '8px 12px',
                            fontSize: '12px',
                            borderBottom: '1px solid #E5E7EB'
                          }}>
                            {ratingLabel}
                          </td>
                        </tr>
                      );
                    });
                    })()}
                  </tbody>
                </table>
              </div>
              {filtered.length === 0 && (
                <div style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#111827',
                  textAlign: 'center',
                  marginTop: '16px'
                }}>
                  {participantsSearchQuery.trim() ? 'No participants found matching your search.' : 'No participants to display.'}
                </div>
              )}
            </div>
          );
        })()}

        {/* Schedule Tab */}
        {activeTab === 'schedule' && toggles.showSchedule && tabContentCheckers.schedule() && (
          <div className="max-w-4xl mx-auto overflow-hidden">
            <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
              <div className="bg-chess-cream" style={{
                borderRadius: '12px',
                border: '1px solid #E5E7EB',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
                padding: '24px',
                marginBottom: '24px',
                maxWidth: '850px',
                marginLeft: 'auto',
                marginRight: 'auto'
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  textAlign: 'center',
                  marginBottom: '16px',
                  color: '#111827'
                }}>Schedule</h3>
                
                {/* Custom Schedule Instructions */}
                {hasContent(data.scheduleInstructions) && (
                  <div style={{
                    fontSize: '16px',
                    color: '#111827',
                    textAlign: 'center',
                    marginBottom: '20px'
                  }}>
                    {renderSimpleFormattedText(data.scheduleInstructions)}
                  </div>
                )}

                {/* Schedule Content */}
                {(() => {
                  const useInternal = data.useInternalScheduler !== false;
                  const rounds = Array.isArray(data.rounds) ? data.rounds : [];
                  const hasInternalRounds = useInternal && rounds.length > 0;
                  const hasExternalLink = typeof data.scheduleLink === 'string' && data.scheduleLink.trim() !== '';
                  
                  // External URL: Show button only if there's actually a link
                  if (hasExternalLink) {
                    let link = data.scheduleLink.trim();
                    if (!/^https?:\/\//i.test(link)) {
                      link = 'https://' + link;
                    }
                    return (
                      <div style={{ textAlign: 'center' }}>
                        <a
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            backgroundColor: data.primaryColorHex || '#3B82F6',
                            color: 'white',
                            fontWeight: 600,
                            padding: '12px 24px',
                            fontSize: '16px',
                            borderRadius: '8px',
                            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
                            cursor: 'pointer',
                            textDecoration: 'none',
                            display: 'inline-block'
                          }}
                        >
                          View Schedule <i className="fa-solid fa-arrow-up-right-from-square ml-1"></i>
                        </a>
                      </div>
                    );
                  }
                  
                  // Internal schedule: Show table
                  if (hasInternalRounds) {
                    return (
                      <div className="overflow-x-auto -mx-2 sm:mx-0">
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '500px' }}>
                          <thead>
                            <tr>
                              <th style={{
                                backgroundColor: '#f7f4eb',
                                fontWeight: '600',
                                padding: '8px 12px',
                                fontSize: '12px',
                                textAlign: 'center',
                                borderBottom: '1px solid #E5E7EB'
                              }}>Round</th>
                              <th style={{
                                backgroundColor: '#f7f4eb',
                                fontWeight: '600',
                                padding: '8px 12px',
                                fontSize: '12px',
                                textAlign: 'center',
                                borderBottom: '1px solid #E5E7EB'
                              }}>Date</th>
                              <th style={{
                                backgroundColor: '#f7f4eb',
                                fontWeight: '600',
                                padding: '8px 12px',
                                fontSize: '12px',
                                textAlign: 'center',
                                borderBottom: '1px solid #E5E7EB'
                              }}>Time</th>
                              <th style={{
                                backgroundColor: '#f7f4eb',
                                fontWeight: '600',
                                padding: '8px 12px',
                                fontSize: '12px',
                                textAlign: 'center',
                                borderBottom: '1px solid #E5E7EB'
                              }}>Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rounds.map((r, idx) => (
                              <tr key={idx}>
                                <td style={{
                                  backgroundColor: '#f7f4eb',
                                  padding: '8px 12px',
                                  fontSize: '12px',
                                  textAlign: 'center',
                                  borderBottom: '1px solid #E5E7EB'
                                }}>{r.round}</td>
                                <td style={{
                                  backgroundColor: '#f7f4eb',
                                  padding: '8px 12px',
                                  fontSize: '12px',
                                  textAlign: 'center',
                                  borderBottom: '1px solid #E5E7EB'
                                }}>{r.date}</td>
                                <td style={{
                                  backgroundColor: '#f7f4eb',
                                  padding: '8px 12px',
                                  fontSize: '12px',
                                  textAlign: 'center',
                                  borderBottom: '1px solid #E5E7EB'
                                }}>{r.time}</td>
                                <td style={{
                                  backgroundColor: '#f7f4eb',
                                  padding: '8px 12px',
                                  fontSize: '12px',
                                  textAlign: 'center',
                                  borderBottom: '1px solid #E5E7EB'
                                }}>{r.notes}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  }
                  
                  // If only custom instructions and no schedule content, return null for this section
                  return null;
                })()}
              </div>
            </div>
          </div>
        )}

        {/* About Tab */}
        {activeTab === 'about' && toggles.showAbout && tabContentCheckers.about() && (
          <div className="max-w-4xl mx-auto overflow-hidden">
            <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
              
              {/* Custom Mode Content */}
              {data.tabCustomModes?.about && hasContent(data.tabModeAboutContent) ? (
                <div className="bg-chess-cream" style={{
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
                  padding: '24px',
                  marginBottom: '24px',
                  maxWidth: '850px',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  textAlign: 'center'
                }}>
                  {renderSimpleFormattedText(data.tabModeAboutContent)}
                </div>
              ) : (
                <>
              <div className="bg-chess-cream" style={{
                        borderRadius: '12px',
                border: '1px solid #E5E7EB',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
                padding: '24px',
                marginBottom: '24px',
                maxWidth: '850px',
                marginLeft: 'auto',
                marginRight: 'auto'
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  textAlign: 'center',
                  marginBottom: '16px',
                  color: '#111827'
                }}>Description</h3>
                <div style={{
                  fontSize: '16px',
                  color: '#111827',
                  textAlign: 'center'
                }}>
                  {data.eventDescription}
                </div>
              </div>
                </>
              )}
              {/* Custom Sections for About Tab */}
              <CustomSectionsRenderer 
                sections={data.aboutCustomSections || []} 
                primaryColorHex={data.primaryColorHex} 
              />
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default PreviewSection; 

// Helper to darken a hex color
function darkenColor(hex, amount = 0.1) {
  // Convert hex to RGB
  let c = hex.replace('#', '');
  if (c.length === 3) c = c.split('').map(x => x + x).join('');
  const num = parseInt(c, 16);
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;
  r = Math.max(0, Math.floor(r * (1 - amount)));
  g = Math.max(0, Math.floor(g * (1 - amount)));
  b = Math.max(0, Math.floor(b * (1 - amount)));
  return `rgb(${r}, ${g}, ${b})`;
} 
